// ====================================================
// src/components/block/liveStreamManagement/enchainements/DirectionPicker.tsx
//
// Cercle + flèche : clic ou glisser pour choisir une direction (0-359°,
// 0 = haut, sens horaire). Utilisé par renderControls des enchaînements à
// direction (Poussé, Recouvrement) — cf. directionUtils.ts pour la config.
// ====================================================

import React, { useCallback, useRef } from 'react';

export interface DirectionPickerProps {
  angleDeg: number;
  onChange: (angleDeg: number) => void;
  size?: number;
}

const DirectionPicker: React.FC<DirectionPickerProps> = ({ angleDeg, onChange, size = 64 }) => {
  const circleRef = useRef<HTMLDivElement>(null);

  const updateFromEvent = useCallback((clientX: number, clientY: number) => {
    const rect = circleRef.current?.getBoundingClientRect();
    if (!rect) return;
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = clientX - cx;
    const dy = clientY - cy;
    if (dx === 0 && dy === 0) return;
    let deg = Math.atan2(dx, -dy) * (180 / Math.PI);
    if (deg < 0) deg += 360;
    onChange(Math.round(deg));
  }, [onChange]);

  const startDrag = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    const point = 'touches' in e ? e.touches[0] : e;
    updateFromEvent(point.clientX, point.clientY);
    const onMove = (ev: MouseEvent | TouchEvent) => {
      const p = 'touches' in ev ? ev.touches[0] : ev;
      updateFromEvent(p.clientX, p.clientY);
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove as EventListener);
      window.removeEventListener('touchend', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove as EventListener, { passive: false });
    window.addEventListener('touchend', onUp);
  }, [updateFromEvent]);

  const rad = (angleDeg * Math.PI) / 180;
  const r  = size / 2 - 10;
  const cx = size / 2, cy = size / 2;
  const ax = cx + Math.sin(rad) * r;
  const ay = cy - Math.cos(rad) * r;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <div
        ref={circleRef}
        onMouseDown={startDrag}
        onTouchStart={startDrag}
        title="Cliquer ou glisser pour choisir la direction"
        style={{
          position: 'relative', width: size, height: size, borderRadius: '50%',
          background: 'radial-gradient(circle, #1e293b, #0f172a)',
          border: '1px solid rgba(255,255,255,0.18)', cursor: 'pointer', flexShrink: 0,
          userSelect: 'none',
        }}
      >
        <svg width={size} height={size} style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
          <circle cx={cx} cy={cy} r={2} fill="rgba(255,255,255,0.3)" />
          <line x1={cx} y1={cy} x2={ax} y2={ay} stroke="#34d399" strokeWidth={2} strokeLinecap="round" />
          <circle cx={ax} cy={ay} r={4} fill="#34d399" />
        </svg>
      </div>
      <span style={{ fontSize: 12, fontFamily: 'monospace', color: '#cbd5e1', minWidth: 32 }}>{Math.round(angleDeg)}°</span>
    </div>
  );
};

export default DirectionPicker;
